import type { KeyboardEvent } from 'react';
import { useRef } from 'react';
import { ChevronDown, type LucideIcon } from 'lucide-react';

import styles from './InfoPanelTabBar.module.css';

export type QuestionInfoPanelTabId = 'hint' | 'practice' | 'solution';

export interface QuestionInfoPanelTab {
  icon: LucideIcon;
  id: QuestionInfoPanelTabId;
  label: string;
}

interface InfoPanelTabBarProps {
  activeTab: QuestionInfoPanelTabId | null;
  getPanelId: (tabId: QuestionInfoPanelTabId) => string;
  getTabId: (tabId: QuestionInfoPanelTabId) => string;
  label?: string;
  onTabSelect: (tabId: QuestionInfoPanelTabId) => void;
  tabs: QuestionInfoPanelTab[];
}

export function InfoPanelTabBar({
  activeTab,
  getPanelId,
  getTabId,
  label = 'Question help options',
  onTabSelect,
  tabs,
}: InfoPanelTabBarProps) {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  function focusTab(index: number) {
    tabRefs.current[index]?.focus();
  }

  function handleKeyDown(
    event: KeyboardEvent<HTMLButtonElement>,
    currentIndex: number,
  ) {
    if (tabs.length === 0) {
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusTab((currentIndex + 1) % tabs.length);
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusTab((currentIndex - 1 + tabs.length) % tabs.length);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      focusTab(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      focusTab(tabs.length - 1);
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onTabSelect(tabs[currentIndex].id);
    }
  }

  return (
    <div aria-label={label} className={styles.tabBar} role="tablist">
      {tabs.map((tab, index) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;

        return (
          <button
            aria-controls={getPanelId(tab.id)}
            aria-selected={isActive}
            className={styles.tab}
            data-active={isActive ? 'true' : 'false'}
            id={getTabId(tab.id)}
            key={tab.id}
            onClick={() => onTabSelect(tab.id)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            ref={(element) => {
              tabRefs.current[index] = element;
            }}
            role="tab"
            type="button"
          >
            <Icon aria-hidden="true" className={styles.icon} size={16} />
            <span className={styles.label}>{tab.label}</span>
            <ChevronDown
              aria-hidden="true"
              className={styles.chevron}
              data-expanded={isActive ? 'true' : 'false'}
              size={14}
            />
          </button>
        );
      })}
    </div>
  );
}
